import { useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  BookOpen,
  FileEdit,
  Megaphone,
  BarChart3,
  Terminal,
  CreditCard,
  TrendingUp,
  Building2,
  Menu,
  X,
  Layers,
  Search,
  FileStack,
  Mail,
} from 'lucide-react';

export function AdminLayout() {
  const loc = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const nav = [
    { to: '/admin', label: 'Overzicht', icon: LayoutDashboard, end: true },
    { to: '/admin/meldingen', label: 'Sitewijde melding', icon: Megaphone, end: false },
    { to: '/admin/cms', label: 'CMS', icon: FileEdit, end: false },
    { to: '/admin/analytics', label: 'Analytics', icon: BarChart3, end: false },
    { to: '/admin/seo-health', label: 'SEO health', icon: Search, end: false },
    { to: '/admin/seo-pages', label: 'SEO pagina’s', icon: FileStack, end: false },
    { to: '/admin/library-coverage', label: 'Library-dekking', icon: Layers, end: false },
    { to: '/admin/contract-status', label: 'Contractstatus', icon: BookOpen, end: false },
    { to: '/admin/billing', label: 'Facturatie', icon: CreditCard, end: false },
    { to: '/admin/billing-platform' as const, label: 'Platform billing', icon: TrendingUp, end: false },
    { to: '/admin/tenants' as const, label: 'Tenants', icon: Building2, end: false },
    { to: '/admin/uitnodigingen' as const, label: 'Uitnodigingen', icon: Mail, end: false },
    { to: '/admin/api-tools', label: 'API-overzicht', icon: Terminal, end: false },
  ];

  const NavInner = ({ onNavigate }: { onNavigate?: () => void }) => (
    <nav className="space-y-0.5">
      {nav.map(({ to, label, icon: Icon, end }) => {
        const active = end ? loc.pathname === to : loc.pathname === to || (to !== '/admin' && loc.pathname.startsWith(to));
        return (
          <Link
            key={to}
            to={to}
            onClick={onNavigate}
            className={`flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm transition ${
              active ? 'bg-primary font-medium text-white' : 'text-text-muted hover:bg-surface hover:text-text'
            }`}
          >
            <Icon className="h-4 w-4 shrink-0" />
            {label}
          </Link>
        );
      })}
    </nav>
  );

  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:gap-6">
      <div className="flex items-center justify-between gap-3 lg:hidden">
        <h1 className="text-lg font-semibold text-text">Admin</h1>
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          className="inline-flex items-center gap-2 rounded-lg border border-border bg-surface-card px-3 py-2 text-sm font-medium text-text"
          aria-expanded={drawerOpen}
        >
          <Menu className="h-5 w-5" />
          Menu
        </button>
      </div>

      {drawerOpen && (
        <button
          type="button"
          className="fixed inset-0 z-[70] bg-black/50 lg:hidden"
          aria-label="Menu sluiten"
          onClick={() => setDrawerOpen(false)}
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-[80] w-[min(18rem,88vw)] border-r border-border bg-surface-card p-4 shadow-xl transition-transform duration-200 lg:static lg:z-0 lg:w-56 lg:shrink-0 lg:rounded-xl lg:border lg:shadow-none ${
          drawerOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <div className="mb-4 flex items-center justify-between lg:block">
          <h2 className="font-semibold text-text lg:mb-4">Platform</h2>
          <button
            type="button"
            className="rounded-lg p-2 text-text-muted hover:bg-surface lg:hidden"
            onClick={() => setDrawerOpen(false)}
            aria-label="Sluiten"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <NavInner onNavigate={() => setDrawerOpen(false)} />
      </aside>

      <main className="min-w-0 flex-1 overflow-x-auto pb-8 lg:pb-0">
        <Outlet />
      </main>
    </div>
  );
}
